import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@astro/api'
import { toIsoDate, formatTimeLabel, isPastTime } from '../../../utils/date'

/**
 * Verified against the live response: { status, duration, rate, slots: [...] }
 * — the array lives under `slots`, not `data`. Also tolerate a bare array or
 * a `data`-keyed envelope in case the shape ever changes.
 */
export function unwrapSlots(response) {
  if (Array.isArray(response)) return response
  if (response && typeof response === 'object') {
    if (Array.isArray(response.slots)) return response.slots
    if (Array.isArray(response.data)) return response.data
  }
  return []
}

/** Shared "choose a time" card — used by both the regular per-astrologer
 * booking flow and the Maharaj tiered-pricing flow, so the slot-fetch +
 * render logic (and the real slot `id` the booking-create payload needs)
 * only lives in one place. `source="gemstone"` reads Raju Maharaj's gemstone
 * consultation slots instead (every day, no astrologer id needed). */
export default function TimeSlotPicker({ astrologerId, selectedDate, selectedSlot, onSelectSlot, source = 'astrologer' }) {
  const isoDate = selectedDate ? toIsoDate(selectedDate) : null
  const isGemstone = source === 'gemstone'

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: isGemstone ? ['gemstone-slots', isoDate] : ['available-slots', astrologerId, isoDate],
    queryFn: ({ signal }) =>
      isGemstone
        ? api.bookings.gemstoneSlots({ date: isoDate }, { signal })
        : api.bookings.availableSlots({ astrologer_id: astrologerId, date: isoDate }, { signal }),
    enabled: Boolean((isGemstone || astrologerId) && isoDate),
  })
  const slots = unwrapSlots(data)

  // Once live slots arrive, drop any selected slot that's no longer bookable
  // on this date (date has no slots, slot vanished, or its time has passed) —
  // otherwise a stale pick keeps the summary card's proceed button unlocked.
  useEffect(() => {
    if (!selectedSlot || isPending || isError) return
    const selectedKey = selectedSlot.id ?? selectedSlot.start_time ?? selectedSlot.time
    const stillBookable = slots.some((slot) => {
      const time = slot.start_time ?? slot.time
      return (slot.id ?? time) === selectedKey && slot.available !== false && !isPastTime(selectedDate, time)
    })
    if (!stillBookable) onSelectSlot(null)
  }, [selectedSlot, slots, isPending, isError, selectedDate, onSelectSlot])

  return (
    <div className="info-card overflow-hidden">
      <div className="h-[3px] w-full bg-gradient-to-r from-[#C8922A] via-[#E5A820] to-[#C8922A]" />
      <div className="p-6 border-b border-[#D7C29A]/20 bg-gradient-to-r from-[#FFFCF5] to-[#FEF6E4] flex items-center gap-3">
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary/10 text-secondary">
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2" />
          </svg>
        </span>
        <div>
          <span className="text-[10px] font-mono font-normal text-secondary uppercase tracking-[0.3em]">Cosmic Window</span>
          <h4 className="text-base font-cinzel text-[#1C140A]">Choose a time</h4>
        </div>
      </div>
      <div className="p-6 space-y-4">
        {!selectedDate && (
          <p className="text-xs text-[#8B7355] italic">Select a date above to unlock the time slots.</p>
        )}
        {selectedDate && isPending && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 animate-pulse" aria-label="Checking live availability">
            {Array.from({ length: 8 }, (_, i) => (
              <div key={i} className="h-11 rounded-xl" style={{ background: 'rgba(200,146,42,.10)' }} />
            ))}
          </div>
        )}
        {selectedDate && isError && (
          <div className="flex items-center justify-between gap-3 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
            <span>Couldn&rsquo;t load slots for this date.</span>
            <button type="button" onClick={() => refetch()} className="font-bold uppercase tracking-widest text-[10px] underline shrink-0">Retry</button>
          </div>
        )}
        {selectedDate && !isPending && !isError && slots.length === 0 && (
          <p className="text-xs text-[#8B7355] italic">No slots available on this date — try another day.</p>
        )}
        {selectedDate && !isPending && !isError && slots.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {slots.map((slot, index) => {
              // Live API returns { id, start_time, end_time } — the booking-create
              // payload needs the real slot `id`, not just its time string.
              const time = slot.start_time ?? slot.time
              // Slots earlier than "now" on today's date can't be booked.
              const isAvailable = slot.available !== false && !isPastTime(selectedDate, time)
              const isActive = selectedSlot ? (selectedSlot.id ?? selectedSlot.start_time ?? selectedSlot.time) === (slot.id ?? time) : false
              return (
                <button
                  key={slot.id ?? time ?? index}
                  type="button"
                  disabled={!isAvailable}
                  onClick={() => onSelectSlot(slot)}
                  className={`slot-btn${isActive ? ' active' : ''}${!isAvailable ? ' disabled' : ''}`}
                >
                  {formatTimeLabel(time)}
                </button>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
